import * as z from "zod";
import {
  ANALYTICS_QUERY_LIMITS,
  ANALYTICS_QUERY_VERSION,
  AnalyticsDirectionSchema,
} from "./analytics-query-constants";
import { AnalyticsExpressionSchema } from "./analytics-query-expression-schema";
import { analyticsQueryReferenceProblems } from "./analytics-query-references";
import {
  AnalyticsLocalNameSchema,
  AnalyticsValueNameSchema,
} from "./analytics-query-refs";
import type {
  AnalyticsColumn,
  AnalyticsDefinition,
  AnalyticsQuery,
  AnalyticsSortTerm,
} from "./analytics-query-types";

/**
 * Structural schema of the query. Catalog name existence, types, and grain
 * are the resolution layer's job.
 */
export const AnalyticsLabelSchema = z
  .string()
  .min(1)
  .max(ANALYTICS_QUERY_LIMITS.labelMax);

export const AnalyticsDefinitionSchema = z.strictObject({
  name: AnalyticsLocalNameSchema,
  expression: AnalyticsExpressionSchema,
  description: z.string().max(ANALYTICS_QUERY_LIMITS.textMax).optional(),
}) satisfies z.ZodType<AnalyticsDefinition>;

export const AnalyticsColumnSchema = z.strictObject({
  name: AnalyticsValueNameSchema,
  label: AnalyticsLabelSchema.optional(),
}) satisfies z.ZodType<AnalyticsColumn>;

export const AnalyticsSortTermSchema = z.strictObject({
  name: AnalyticsValueNameSchema,
  direction: AnalyticsDirectionSchema,
}) satisfies z.ZodType<AnalyticsSortTerm>;

/**
 * A whole-query refinement JSON Schema cannot carry. Resolution backs it with DefinitionsAcyclic,
 * DefinitionNamesUnique, LocalNamesDefined, ColumnNamesUnique and GrainKeysUnshown.
 */
export const AnalyticsQuerySchema = z
  .strictObject({
    version: z.literal(ANALYTICS_QUERY_VERSION),
    definitions: z
      .array(AnalyticsDefinitionSchema)
      .max(ANALYTICS_QUERY_LIMITS.definitionsMax),
    columns: z
      .array(AnalyticsColumnSchema)
      .min(1)
      .max(ANALYTICS_QUERY_LIMITS.columnsMax),
    filters: z
      .array(AnalyticsValueNameSchema)
      .max(ANALYTICS_QUERY_LIMITS.filtersMax)
      .optional(),
    filtersAfterWindows: z
      .array(AnalyticsValueNameSchema)
      .max(ANALYTICS_QUERY_LIMITS.filtersMax)
      .optional(),
    grainKeys: z
      .array(AnalyticsValueNameSchema)
      .max(ANALYTICS_QUERY_LIMITS.columnsMax)
      .optional(),
    sorts: z
      .array(AnalyticsSortTermSchema)
      .max(ANALYTICS_QUERY_LIMITS.sortsMax)
      .optional(),
    limit: z.int().min(1).optional(),
  })
  .superRefine((query, ctx) => {
    for (const { path, message } of analyticsQueryReferenceProblems(query)) {
      ctx.addIssue({ code: "custom", path: [...path], message, input: query });
    }
  }) satisfies z.ZodType<AnalyticsQuery>;

/** The one parse choke point for the structural layer. */
export function parseAnalyticsQuery(query: unknown): AnalyticsQuery {
  return AnalyticsQuerySchema.parse(query);
}
